import {useCallback, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {DocumentSnapshot} from 'firebase/firestore';
import {
    FeedbackPage,
    getFeedbackById,
    getFeedbackList,
    getOtherFeedbacksByUid,
    getUniqueAssignees,
    getUniquePlatforms,
    getUniqueVersions,
    getUserInfo,
    getUserPurchases,
    resolveEmailToUid,
    updateFeedbackDetails,
    UserInfo,
    UserPurchase,
} from '../services/feedbackService';
import {
    FeedbackCategory,
    FeedbackFilters,
    FeedbackReport,
    FeedbackStatus,
    FeedbackTag,
    FeedbackTeam
} from '../types/feedback';

// Parse filters from URL search params
function parseFiltersFromUrl(searchParams: URLSearchParams): FeedbackFilters {
    const filters: FeedbackFilters = {};

    const status = searchParams.get('status');
    if (status) filters.status = status as FeedbackStatus;

    const category = searchParams.get('category');
    if (category) filters.category = category as FeedbackCategory;

    const team = searchParams.get('team');
    if (team) filters.team = team as FeedbackTeam;

    const tag = searchParams.get('tag');
    if (tag) filters.tag = tag as FeedbackTag;

    const platform = searchParams.get('platform');
    if (platform) filters.platform = platform;

    const assignee = searchParams.get('assignee');
    if (assignee) filters.assignee = assignee;

    const betaEnabled = searchParams.get('betaEnabled');
    if (betaEnabled) filters.betaEnabled = betaEnabled === 'true';

    const searchQuery = searchParams.get('q');
    if (searchQuery) filters.searchQuery = searchQuery;

    const startDate = searchParams.get('startDate');
    if (startDate) filters.startDate = new Date(startDate);

    const endDate = searchParams.get('endDate');
    if (endDate) filters.endDate = new Date(endDate);

    const uid = searchParams.get('uid');
    if (uid) filters.uid = uid;

    const fromVersion = searchParams.get('fromVersion');
    if (fromVersion) filters.fromVersion = fromVersion;

    const unread = searchParams.get('unread');
    if (unread === 'true') filters.hasUnreadUserReply = true;

    return filters;
}

// Serialize filters to URL search params
function serializeFiltersToUrl(filters: FeedbackFilters): URLSearchParams {
    const params = new URLSearchParams();

    if (filters.status) params.set('status', filters.status);
    if (filters.category) params.set('category', filters.category);
    if (filters.team) params.set('team', filters.team);
    if (filters.tag) params.set('tag', filters.tag);
    if (filters.platform) params.set('platform', filters.platform);
    if (filters.assignee) params.set('assignee', filters.assignee);
    if (filters.betaEnabled !== undefined) params.set('betaEnabled', String(filters.betaEnabled));
    if (filters.searchQuery) params.set('q', filters.searchQuery);
    if (filters.startDate) params.set('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.set('endDate', filters.endDate.toISOString());
    if (filters.uid) params.set('uid', filters.uid);
    if (filters.fromVersion) params.set('fromVersion', filters.fromVersion);
    if (filters.hasUnreadUserReply) params.set('unread', 'true');

    return params;
}

// Session storage keys
const STORAGE_KEY_ITEMS = 'feedback_list_items';
const STORAGE_KEY_HAS_MORE = 'feedback_list_hasMore';
const STORAGE_KEY_SCROLL = 'feedback_list_scroll';
const STORAGE_KEY_FILTERS_HASH = 'feedback_list_filters_hash';

// Create a hash of filters to detect changes
function getFiltersHash(filters: FeedbackFilters): string {
    return JSON.stringify(filters);
}

// Save items to session storage
function saveToSession(items: FeedbackReport[], hasMore: boolean, filtersHash: string) {
    try {
        sessionStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(items));
        sessionStorage.setItem(STORAGE_KEY_HAS_MORE, JSON.stringify(hasMore));
        sessionStorage.setItem(STORAGE_KEY_FILTERS_HASH, filtersHash);
    } catch {
        // Ignore storage errors
    }
}

// Load items from session storage
function loadFromSession(filtersHash: string): { items: FeedbackReport[]; hasMore: boolean } | null {
    try {
        const storedHash = sessionStorage.getItem(STORAGE_KEY_FILTERS_HASH);
        if (storedHash !== filtersHash) {
            return null; // Filters changed, don't restore
        }
        const itemsJson = sessionStorage.getItem(STORAGE_KEY_ITEMS);
        const hasMoreJson = sessionStorage.getItem(STORAGE_KEY_HAS_MORE);
        if (itemsJson && hasMoreJson) {
            return {
                items: JSON.parse(itemsJson),
                hasMore: JSON.parse(hasMoreJson),
            };
        }
    } catch {
        // Ignore storage errors
    }
    return null;
}

// Save scroll position
function saveScrollPosition() {
    try {
        sessionStorage.setItem(STORAGE_KEY_SCROLL, String(window.scrollY));
    } catch {
        // Ignore storage errors
    }
}

// Restore scroll position
function restoreScrollPosition() {
    try {
        const scrollY = sessionStorage.getItem(STORAGE_KEY_SCROLL);
        if (scrollY) {
            setTimeout(() => window.scrollTo(0, parseInt(scrollY, 10)), 0);
        }
    } catch {
        // Ignore storage errors
    }
}

export function useFeedbackList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState<FeedbackFilters>(() => parseFiltersFromUrl(searchParams));
    const filtersHash = getFiltersHash(filters);

    // Try to restore from session on initial load
    const [items, setItems] = useState<FeedbackReport[]>(() => {
        const cached = loadFromSession(filtersHash);
        return cached?.items ?? [];
    });
    const [hasMore, setHasMore] = useState(() => {
        const cached = loadFromSession(filtersHash);
        return cached?.hasMore ?? false;
    });
    const [loading, setLoading] = useState(() => {
        const cached = loadFromSession(filtersHash);
        return !cached; // Only loading if no cache
    });
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
    const [initialized, setInitialized] = useState(() => {
        const cached = loadFromSession(filtersHash);
        return !!cached;
    });

    // Restore scroll position on mount if we have cached items
    useEffect(() => {
        if (initialized && items.length > 0) {
            restoreScrollPosition();
        }
    }, []);

    // Save scroll position before navigating away
    useEffect(() => {
        const handleBeforeUnload = () => saveScrollPosition();
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Also save when clicking on items
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('a[href^="/feedback/"], tr[class*="cursor-pointer"]')) {
                saveScrollPosition();
            }
        };
        document.addEventListener('click', handleClick, true);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleClick, true);
        };
    }, []);

    // Sync filters to URL when they change
    useEffect(() => {
        const newParams = serializeFiltersToUrl(filters);
        setSearchParams(newParams, {replace: true});
    }, [filters, setSearchParams]);

    // Save items to session when they change
    useEffect(() => {
        if (items.length > 0) {
            saveToSession(items, hasMore, filtersHash);
        }
    }, [items, hasMore, filtersHash]);

  const fetchFeedback = useCallback(async (reset = false) => {
    setLoading(true);
    setError(null);
    try {
      // If search query looks like an email, resolve to uid
      let effectiveFilters = filters;
      if (filters.searchQuery && filters.searchQuery.includes('@')) {
        const typed = filters.searchQuery.trim();
        const lookup = await resolveEmailToUid(typed);
        if (lookup.kind === 'found') {
          effectiveFilters = { ...filters, uid: lookup.uid, searchQuery: undefined };
        } else {
          // Aucun champ d'un signalement ne contient d'adresse e-mail : le
          // repli en recherche texte ne pouvait renvoyer que zero resultat,
          // sans jamais distinguer « cette personne n'a rien envoye » de
          // « la resolution a echoue ». On s'arrete et on dit lequel.
          setItems([]);
          setLastDoc(null);
          setHasMore(false);
          setError(
            lookup.kind === 'no-account'
              ? `Aucun compte ne correspond a ${typed}. L'adresse doit etre celle du compte dans l'application, qui peut differer de celle utilisee pour ecrire.`
              : `Recherche par e-mail indisponible : ${lookup.message}. Reessayez.`
          );
          return;
        }
      }

      const page: FeedbackPage = await getFeedbackList(
        effectiveFilters,
        reset ? undefined : lastDoc ?? undefined
      );
      if (reset) {
        setItems(page.items);
      } else {
        setItems((prev) => [...prev, ...page.items]);
      }
      setLastDoc(page.lastDoc);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
        setInitialized(true);
    }
  }, [filters, lastDoc]);

  useEffect(() => {
      // Only fetch if not initialized from cache or filters changed
      if (!initialized) {
          fetchFeedback(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    // When filters change, reset and fetch
    useEffect(() => {
        if (initialized) {
            setItems([]);
            setLastDoc(null);
            fetchFeedback(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersHash]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      fetchFeedback(false);
    }
  }, [hasMore, loading, fetchFeedback]);

  const refresh = useCallback(() => {
    setItems([]);
    setLastDoc(null);
    fetchFeedback(true);
  }, [fetchFeedback]);

  const updateFilters = useCallback((newFilters: Partial<FeedbackFilters>) => {
      setFilters((prev) => {
          // Merge new filters with previous, but remove keys that are explicitly set to undefined
          const merged = {...prev, ...newFilters};
          // Clean up undefined values to keep the object clean
          Object.keys(merged).forEach((key) => {
              if (merged[key as keyof FeedbackFilters] === undefined) {
                  delete merged[key as keyof FeedbackFilters];
              }
          });
          return merged;
      });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    filters,
    updateFilters,
    clearFilters,
  };
}

export function useFeedbackDetail(id: string | undefined) {
  const [feedback, setFeedback] = useState<FeedbackReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) {
      setFeedback(null);
      setLoading(false);
      return;
    }

    const fetchFeedback = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getFeedbackById(id);
        setFeedback(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [id]);

  const updateFeedback = useCallback(
    async (
      updates: {
        status?: FeedbackStatus;
          team?: FeedbackTeam | null;
        internalNote?: string;
        assignee?: string;
          tags?: FeedbackTag[];
      },
      updatedBy: string
    ) => {
      if (!id) return;

      setSaving(true);
      setError(null);
      try {
        await updateFeedbackDetails(id, updates, updatedBy);
        setFeedback((prev) =>
          prev
            ? {
                ...prev,
                ...updates,
                  // Convert null to undefined for team (FeedbackReport.team doesn't accept null)
                  team: updates.team === null ? undefined : (updates.team ?? prev.team),
              }
            : null
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update feedback');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [id]
  );

  return {
    feedback,
    loading,
    error,
    saving,
    updateFeedback,
  };
}

export function useFilterOptions() {
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
    const [versions, setVersions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
          const [platformsList, assigneesList, versionsList] = await Promise.all([
          getUniquePlatforms(),
          getUniqueAssignees(),
              getUniqueVersions(),
        ]);
        setPlatforms(platformsList);
        setAssignees(assigneesList);
          setVersions(versionsList);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

    return {platforms, assignees, versions, loading};
}

export function useUserPurchases(uid: string | undefined) {
  const [purchases, setPurchases] = useState<UserPurchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setPurchases([]);
      return;
    }

    const fetchPurchases = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getUserPurchases(uid);
        setPurchases(data);
      } catch (err) {
        console.error('Failed to fetch user purchases:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch purchases');
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [uid]);

  return { purchases, loading, error };
}

export function useUserInfo(uid: string | undefined) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setUserInfo(null);
      return;
    }

    const fetchUserInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getUserInfo(uid);
        setUserInfo(data);
      } catch (err) {
        console.error('Failed to fetch user info:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user info');
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [uid]);

  return { userInfo, loading, error };
}

export function useOtherFeedbacksByUser(uid: string | undefined, excludeId: string | undefined) {
  const [items, setItems] = useState<FeedbackReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid || !excludeId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOtherFeedbacksByUid(uid, excludeId)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch other feedbacks:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch other feedbacks');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, excludeId]);

  return { items, loading, error };
}
